/**
 * Configurazione principale dell'applicazione
 * Centralizza tutti i parametri di configurazione per diversi ambienti
 */

export interface IMqttConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  protocol: "ws" | "wss";
}

export interface IApiConfig {
  baseUrl: string;
  sentinelsUrl: string;
  sensorsUrl: string;
}

export interface IWebRtcConfig {
  fallbackHost: string;
}

export interface IVideoSdkConfig {
  host: string;
  port: number;
  relay: boolean;   // true in cloud: il video passa dal relay sul server, il browser conosce solo l'id della sentinella
}

export interface IAudioSdkConfig {
  host: string;
  port: number;
}

export interface IWebSocketConfig {
  enabled: boolean;              // Abilita/disabilita WebSocket
  url: string;                   // URL del server Socket.IO (es: http://192.168.1.40:5000)
  reconnectionAttempts: number;  // Numero massimo tentativi riconnessione
  reconnectionDelay: number;     // Delay in ms tra tentativi riconnessione
  timeout: number;               // Timeout connessione in ms
}

export interface IPollingConfig {
  enabled: boolean;              // Abilita/disabilita HTTP polling
  alarmInterval: number;         // Intervallo polling allarmi (ms)
  sentinelInterval: number;      // Intervallo polling sentinels (ms)
}

export interface ISensorConfig {
  offlineTimeout: number;        // Timeout in secondi per considerare sensore offline
  offlineCheckInterval: number;  // Intervallo in secondi per controllo stato offline
}

export interface INvrConfig {
  enableSceneClip: boolean;      // Abilita preload scena NVR e pulsante "Visualizza scena"
}

export interface IAppConfig {
  mqtt: IMqttConfig;
  api: IApiConfig;
  webrtc: IWebRtcConfig;
  videoSdk: IVideoSdkConfig;
  audioSdk: IAudioSdkConfig;
  websocket: IWebSocketConfig;
  polling: IPollingConfig;
  sensor: ISensorConfig;
  nvr: INvrConfig;
}

/**
 * Configurazione a runtime: public/config.js imposta window.__APP_CONFIG__ e viene servito
 * insieme al build, quindi puo' cambiare per ogni installazione (un file per cliente) senza
 * ricompilare. Le chiavi sono le stesse REACT_APP_* del .env, cosi' esiste una sola lista.
 */
const runtimeConfig = (): Record<string, string | undefined> =>
  (typeof window !== "undefined" && window.__APP_CONFIG__) || {};

/**
 * Ottiene un parametro di configurazione. Ordine: runtime (config.js), build (.env), fallback.
 */
export const getEnvVar = (key: string, fallback: string): string => {
  const value = runtimeConfig()[key] || process.env[key] || fallback;
  // {host} = nome con cui e' stata aperta la pagina: l'installazione locale scrive
  // http://{host}:5000/api, cosi' l'app va dalla LAN, da Tailscale o da qualunque altro indirizzo
  return value.replace(/\{host\}/g, typeof window !== "undefined" ? window.location.hostname : "localhost");
};

/**
 * Origine della pagina (schema + host + porta). In produzione i servizi stanno dietro lo stesso
 * nome del sito, quindi e' il default giusto quando un URL non e' configurato.
 */
const pageOrigin = (): string =>
  typeof window !== "undefined" ? window.location.origin : "http://localhost:5000";
const pageHost = (): string =>
  typeof window !== "undefined" ? window.location.hostname : "localhost";

/**
 * Costruisce la configurazione basata sull'ambiente corrente
 */
export const createAppConfig = (): IAppConfig => {
  const environment =
    typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "development"
      : "production";

  // Log dell'ambiente corrente per debugging
  console.log(
    `🔧 Domino_Health_Digital_Caregiver Config: Caricamento configurazione per ambiente '${environment}'`
  );

  // Estrai host e porta dall'API_BASE_URL per WebSocket
  const apiBaseUrl = getEnvVar("REACT_APP_API_BASE_URL", `${pageOrigin()}/api`);
  const websocketUrl = apiBaseUrl.replace(/\/api$/, ""); // Rimuovi /api per ottenere base URL

  return {
    mqtt: {
      host: getEnvVar("REACT_APP_MQTT_HOST", "localhost"),
      port: parseInt(getEnvVar("REACT_APP_MQTT_PORT", "8083"), 10),
      username: getEnvVar("REACT_APP_MQTT_USERNAME", "user"),
      password: getEnvVar("REACT_APP_MQTT_PASSWORD", ""),
      protocol: getEnvVar("REACT_APP_MQTT_PROTOCOL", "ws") as "ws" | "wss",
    },
    api: {
      baseUrl: apiBaseUrl,
      sentinelsUrl: getEnvVar("REACT_APP_SENTINELS_URL", `${apiBaseUrl}/sentinels`),
      sensorsUrl: getEnvVar("REACT_APP_SENSORS_URL", `${apiBaseUrl}/sensors`),
    },
    webrtc: {
      fallbackHost: getEnvVar("REACT_APP_SERVER_HOST", pageHost()),
    },
    videoSdk: {
      host: getEnvVar("REACT_APP_VIDEO_SDK_HOST", pageHost()),
      port: parseInt(getEnvVar("REACT_APP_VIDEO_SDK_PORT", "5002"), 10),
      relay: getEnvVar("REACT_APP_VIDEO_RELAY", "false") === "true",
    },
    audioSdk: {
      host: getEnvVar("REACT_APP_AUDIO_SDK_HOST", pageHost()),
      port: parseInt(getEnvVar("REACT_APP_AUDIO_SDK_PORT", "5003"), 10),
    },
    websocket: {
      enabled: getEnvVar("REACT_APP_WEBSOCKET_ENABLED", "true") === "true",
      url: getEnvVar("REACT_APP_WEBSOCKET_URL", websocketUrl),
      reconnectionAttempts: parseInt(getEnvVar("REACT_APP_WEBSOCKET_RECONNECTION_ATTEMPTS", "5"), 10),
      reconnectionDelay: parseInt(getEnvVar("REACT_APP_WEBSOCKET_RECONNECTION_DELAY", "1000"), 10),
      timeout: parseInt(getEnvVar("REACT_APP_WEBSOCKET_TIMEOUT", "10000"), 10),
    },
    polling: {
      enabled: getEnvVar("REACT_APP_POLLING_ENABLED", "false") === "true",
      alarmInterval: parseInt(getEnvVar("REACT_APP_ALARM_SCAN_DELAY", "5000"), 10),
      sentinelInterval: parseInt(getEnvVar("REACT_APP_SENTINEL_SCAN_DELAY", "10000"), 10),
    },
    sensor: {
      offlineTimeout: parseInt(getEnvVar("REACT_APP_SENSOR_OFF_TIMEOUT", "30"), 10),
      offlineCheckInterval: parseInt(getEnvVar("REACT_APP_OFFLINE_CHECK_INTERVAL", "10"), 10),
    },
    nvr: {
      enableSceneClip: getEnvVar("REACT_APP_ENABLE_NVR_SCENE_CLIP", "true") === "true",
    },
  };
};

/**
 * Istanza globale della configurazione
 */
export const AppConfig: IAppConfig = createAppConfig();

/**
 * Utility per costruire URL MQTT completo
 */
export const getMqttUrl = (): string => {
  const { mqtt } = AppConfig;
  return `${mqtt.protocol}://${mqtt.host}:${mqtt.port}`;
};

/**
 * Utility per ottenere le credenziali MQTT
 */
export const getMqttCredentials = () => {
  const { mqtt } = AppConfig;
  return {
    username: mqtt.username,
    password: mqtt.password,
  };
};

/**
 * Utility per costruire URL Video SDK completo
 */
export const getVideoSdkUrl = (): string => {
  const { videoSdk } = AppConfig;
  return `http://${videoSdk.host}:${videoSdk.port}`;
};

/**
 * Utility per ottenere la configurazione Video SDK
 */
export const getVideoSdkConfig = () => {
  const { videoSdk } = AppConfig;
  return {
    host: videoSdk.host,
    port: videoSdk.port,
    url: getVideoSdkUrl(),
  };
};

/**
 * Utility per costruire URL Video SDK con camera settings
 * Estrae automaticamente il canale dal cameraUrl e lo usa come camId
 */
export const getVideoSdkUrlWithCamera = (
  settings: string[]
): string => {
  const { videoSdk } = AppConfig;

  // error check
  if (settings.length !== 5) {
    return "";
  }

  const camIp = settings[0];
  const camPort = settings[1];
  const camUser = settings[2];
  const camPassword = settings[3];
  const camChannel = settings[4];

  return `http://${videoSdk.host}:${videoSdk.port}?camId=${camChannel}&ipCam=${camIp}&port=${camPort}&user=${camUser}&password=${camPassword}&camChannel=${camChannel}`;
};

/**
 * URL della pagina video in modalita' relay: stesso sito del frontend (nginx inoltra /videosdk/
 * alla pagina e /video/ al relay). Passa solo l'id della sentinella: niente indirizzi ne'
 * credenziali della telecamera nel browser.
 */
export const getVideoRelayUrl = (sentinelId: number): string => {
  return `${pageOrigin()}/videosdk/relay.html?sentinelId=${sentinelId}`;
};

/**
 * Utility per costruire URL Audio SDK completo
 */
export const getAudioSdkUrl = (): string => {
  const { audioSdk } = AppConfig;
  return `http://${audioSdk.host}:${audioSdk.port}`;
};

/**
 * Utility per ottenere la configurazione Audio SDK
 */
export const getAudioSdkConfig = () => {
  const { audioSdk } = AppConfig;
  return {
    host: audioSdk.host,
    port: audioSdk.port,
    url: getAudioSdkUrl(),
  };
};

/**
 * Utility per ottenere la configurazione WebSocket
 */
export const getWebSocketConfig = () => {
  const { websocket } = AppConfig;
  return {
    enabled: websocket.enabled,
    url: websocket.url,
    reconnectionAttempts: websocket.reconnectionAttempts,
    reconnectionDelay: websocket.reconnectionDelay,
    timeout: websocket.timeout,
  };
};

/**
 * Utility per verificare se WebSocket è abilitato
 */
export const isWebSocketEnabled = (): boolean => {
  return AppConfig.websocket.enabled;
};

/**
 * Utility per verificare se HTTP Polling è abilitato
 */
export const isPollingEnabled = (): boolean => {
  return AppConfig.polling.enabled;
};

/**
 * Utility per ottenere la configurazione Polling
 */
export const getPollingConfig = () => {
  const { polling } = AppConfig;
  return {
    enabled: polling.enabled,
    alarmInterval: polling.alarmInterval,
    sentinelInterval: polling.sentinelInterval,
  };
};

/**
 * Utility per ottenere l'intervallo di polling allarmi
 */
export const getAlarmPollingInterval = (): number => {
  return AppConfig.polling.alarmInterval;
};

/**
 * Utility per ottenere l'intervallo di polling sentinels
 */
export const getSentinelPollingInterval = (): number => {
  return AppConfig.polling.sentinelInterval;
};
